<template>
  <div class="page-wrap" :class="{ night, k3: isK3, fc3d: isFc3d }">
    <header class="topbar">
      <button class="back" type="button" aria-label="返回" @click="goBack">
        <svg viewBox="0 0 24 24">
          <path d="M15.2 5 8.2 12l7 7" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
      <h1>路子图</h1>
    </header>

    <nav class="main-tabs" :class="{ single: mainTabs.length === 1 }">
      <button
        v-for="tab in mainTabs"
        :key="tab.key"
        type="button"
        :class="{ on: mainTab === tab.key }"
        @click="setMain(tab.key)"
      >{{ tab.label }}</button>
    </nav>

    <div class="filters" :class="{ row5: isK3 && mainTab === 'sum', fc3d: isFc3d }">
      <button
        v-for="item in subFilters"
        :key="item.key"
        type="button"
        :class="{ on: filter === item.key }"
        @click="filter = item.key"
      >{{ item.label }}</button>
    </div>

    <div class="boards">
      <div class="board-scroll">
        <table class="board">
          <tbody>
            <tr v-for="(row, r) in bigRoad" :key="'b' + r">
              <td v-if="r === 0" class="road-label" :rowspan="ROWS">大路</td>
              <td v-for="(cell, c) in row" :key="'bc' + c">
                <span v-if="cell" class="dot" :class="[cell.tone, { num: isNum }]">{{ cell.text }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="board-scroll">
        <table class="board">
          <tbody>
            <tr v-for="(row, r) in beadRoad" :key="'d' + r">
              <td v-if="r === 0" class="road-label" :rowspan="ROWS">珠盘路</td>
              <td v-for="(cell, c) in row" :key="'dc' + c">
                <span v-if="cell" class="dot" :class="[cell.tone, { num: isNum }]">{{ cell.text }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: 'RoadmapPage' }
</script>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTheme } from '@/composables/useTheme'
import { useActiveGame } from '@/composables/useActiveGame'
import { getBetPath } from '@/composables/betRoute'
import { games } from '@/data/games'
import { lgwApi } from '@/api/lgw'
import {
  generatePk10Draws,
  generateK3Draws,
  ROAD_MAIN_TABS,
  ROAD_SUM_FILTERS,
  ROAD_DT_FILTERS,
  ROAD_MAIN_TABS_K3,
  ROAD_SUM_FILTERS_K3,
  ROAD_LHH_FILTERS_K3,
  pk10RoadItems,
  k3RoadItems,
  buildPk10BigRoad,
  buildPk10BeadBoard
} from '@/data/bet'
import {
  FC3D_GAME_ID,
  ROAD_MAIN_TABS_FC3D,
  ROAD_FILTERS_FC3D,
  fc3dRoadItems,
  generateFc3dDraws
} from '@/data/fc3d'

const { night } = useTheme()
const route = useRoute()
const router = useRouter()
const { gameId: activeGameId } = useActiveGame()

const ROWS = 6
const COLS = 45

const liveDraws = ref([])
let pollTimer = null

const gameId = computed(() => Number(route.query.id) || activeGameId.value || 1802)
const game = computed(() => games.value.find((g) => g.id === gameId.value) || null)
const isK3 = computed(() => game.value?.group === 'K3' || gameId.value === 1909)
const isFc3d = computed(() => gameId.value === FC3D_GAME_ID || game.value?.name === '福彩3D')

const mainTabs = computed(() => {
  if (isFc3d.value) return ROAD_MAIN_TABS_FC3D
  if (isK3.value) return ROAD_MAIN_TABS_K3
  return ROAD_MAIN_TABS
})
const mainTab = ref('sum')
const filter = ref('gy-bs')

const subFilters = computed(() => {
  if (isFc3d.value) return ROAD_FILTERS_FC3D
  if (isK3.value) {
    return mainTab.value === 'lhh' ? ROAD_LHH_FILTERS_K3 : ROAD_SUM_FILTERS_K3
  }
  return mainTab.value === 'dt' ? ROAD_DT_FILTERS : ROAD_SUM_FILTERS
})

const isNum = computed(() => {
  if (isFc3d.value) return String(filter.value).endsWith('-pos')
  if (isK3.value) return ['b0', 'b1', 'b2', 'hz', 'span'].includes(filter.value)
  return filter.value === 'gy-sum'
})

const draws = computed(() => {
  if (liveDraws.value.length) return liveDraws.value
  if (isFc3d.value) return generateFc3dDraws(gameId.value, 60)
  if (isK3.value) return generateK3Draws(gameId.value, 60)
  return generatePk10Draws(gameId.value, 45)
})

const items = computed(() => {
  if (isFc3d.value) return fc3dRoadItems(draws.value, filter.value)
  if (isK3.value) return k3RoadItems(draws.value, filter.value)
  return pk10RoadItems(draws.value, filter.value)
})

const bigRoad = computed(() => buildPk10BigRoad(items.value, ROWS, COLS))
const beadRoad = computed(() => buildPk10BeadBoard(items.value, ROWS, COLS))

const setMain = (key) => {
  mainTab.value = key
  if (isFc3d.value) {
    filter.value = ROAD_FILTERS_FC3D[0].key
  } else if (isK3.value) {
    filter.value = key === 'lhh' ? ROAD_LHH_FILTERS_K3[0].key : ROAD_SUM_FILTERS_K3[0].key
  } else {
    filter.value = key === 'dt' ? ROAD_DT_FILTERS[0].key : 'gy-bs'
  }
}

const resetFiltersForGame = () => {
  if (isFc3d.value) {
    mainTab.value = 'combine'
    filter.value = ROAD_FILTERS_FC3D[0].key
    return
  }
  mainTab.value = 'sum'
  filter.value = isK3.value ? ROAD_SUM_FILTERS_K3[0].key : 'gy-bs'
}

const normalizeDraws = (page) => {
  const rows = page?.list || page?.content || page?.rows || []
  if (!Array.isArray(rows)) return []
  return rows.map((row) => {
    let codes = []
    if (Array.isArray(row.codes)) {
      codes = row.codes.map((n) => Number(n))
    } else if (row.winNo != null) {
      codes = String(row.winNo).replace(/\D/g, '').split('').map(Number)
    }
    return {
      issue: String(row.numero || row.issue || ''),
      numero: String(row.numero || row.issue || ''),
      codes
    }
  }).filter((r) => r.codes.length)
}

const loadDraws = async () => {
  try {
    const res = await lgwApi.draws(gameId.value)
    if (res?.success && res.data) {
      const list = normalizeDraws(res.data)
      if (list.length) liveDraws.value = list
    }
  } catch {}
}

const goBack = () => {
  if (window.history.length > 1) router.back()
  else router.replace({ path: getBetPath(gameId.value), query: { id: gameId.value } })
}

watch(gameId, () => {
  liveDraws.value = []
  resetFiltersForGame()
  loadDraws()
})

onMounted(() => {
  resetFiltersForGame()
  loadDraws()
  pollTimer = setInterval(loadDraws, 5000)
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<style scoped>
.page-wrap {
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  margin: 0 auto;
  overflow-x: hidden;
  background: #f3f7f9;
  font-family: 'PingFang SC', 'Microsoft YaHei', Avenir, Helvetica, Arial, sans-serif;
  color: #333;
}

.topbar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding-top: env(safe-area-inset-top);
  background: #f42c4c;
}

.topbar h1 {
  margin: 0;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  line-height: 44px;
}

.back {
  position: absolute;
  left: 4px;
  top: env(safe-area-inset-top);
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.back svg {
  width: 22px;
  height: 22px;
  display: block;
  margin: 0 auto;
}

.main-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #fff;
  border-bottom: 1px solid #d1d1d1;
}

.main-tabs.single {
  grid-template-columns: 1fr;
}

.main-tabs button {
  height: 42px;
  border: 0;
  background: transparent;
  color: #888;
  font-size: 15px;
  cursor: pointer;
}

.main-tabs button.on {
  color: #3786ff;
  font-weight: 700;
  box-shadow: inset 0 -3px 0 #3786ff;
}

.filters {
  padding: 8px 12px 4px;
  background: #f3f7f9;
}

.filters.row5 {
  display: flex;
  gap: 6px;
  padding: 8px 10px;
}

.filters.fc3d {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  padding: 10px 12px 8px;
}

.filters button {
  display: inline-block;
  height: 26px;
  margin: 4px 6px 4px 0;
  padding: 0 10px;
  border: 1px solid #5f5f5f;
  border-radius: 4px;
  background: #fff;
  color: #222;
  font-size: 13px;
  line-height: 24px;
  white-space: nowrap;
  cursor: pointer;
}

.filters.fc3d button {
  margin: 0;
  min-width: 58px;
  padding: 0 8px;
  text-align: center;
  border-color: #c8c8c8;
  font-size: 12px;
}

.filters.row5 button {
  flex: 1;
  margin: 0;
  padding: 0 4px;
  text-align: center;
  font-size: 12px;
}

.filters button.on {
  border-color: #3786ff;
  color: #3786ff;
}

.boards {
  width: 100%;
  overflow: hidden;
  background: #fff;
  min-height: 40vh;
}

.board-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 10px;
  background: #fff;
  scrollbar-width: none;
}

.board-scroll::-webkit-scrollbar {
  display: none;
  height: 0;
}

.board {
  table-layout: fixed;
  border-collapse: collapse;
  border-top: 1px solid #bebdbd;
  border-bottom: 1px solid #bebdbd;
}

.board tr:nth-child(odd) {
  background: #fff;
}

.board tr:nth-child(even) {
  background: #eee;
}

.board td {
  box-sizing: border-box;
  width: 22px;
  min-width: 22px;
  max-width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #bebdbd;
  text-align: center;
  vertical-align: middle;
}

.board td.road-label {
  width: 22px;
  min-width: 22px;
  max-width: 22px;
  padding: 4px 2px;
  writing-mode: vertical-rl;
  text-orientation: upright;
  letter-spacing: 2px;
  font-size: 12px;
  color: #555;
  background: #f7f7f7;
  white-space: nowrap;
}

.dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin: 0 auto;
  border-radius: 50%;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}

.dot.num {
  font-size: 10px;
}

.dot.red {
  background: #ec2529;
}

.dot.blue {
  background: #4d84f9;
}

.dot.green {
  background: #2aa86a;
}

.page-wrap.night {
  background: #1a1a1a;
  color: #ddd;
}

.page-wrap.night .main-tabs {
  background: #1a1a1a;
  border-bottom-color: #333;
}

.page-wrap.night .filters,
.page-wrap.night .boards {
  background: #1a1a1a;
}

.page-wrap.night .filters button {
  background: #2a2a2a;
  border-color: #666;
  color: #ddd;
}

.page-wrap.night .board-scroll {
  background: #1a1a1a;
}

.page-wrap.night .board tr:nth-child(odd) {
  background: #1a1a1a;
}

.page-wrap.night .board tr:nth-child(even) {
  background: #242424;
}

.page-wrap.night .board td {
  border-color: #333;
}

.page-wrap.night .board td.road-label {
  background: #222;
  color: #bbb;
}
</style>
